import { useState } from "react";
import {
    FlowDefinition,
    StepResult,
} from "../interfaces/FlowInterfaces";

export const FlowRunner = <StepId extends string, TValues, TExternalState extends object = {}>(
    {
        flow,
        initialState,
        externalState
    }: {
        flow: FlowDefinition<StepId, TValues, TExternalState>,
        initialState?: Partial<TValues>,
        externalState?: TExternalState
    }
) => {

    const [currentStepId, setCurrentStepId] = useState<StepId>(flow.initialStep);
    const [values, setValues] = useState<TValues>((initialState ? initialState : {}) as TValues);

    const step = flow.steps[currentStepId];

    const validation = step.isComplete?.(values as any, values);
    const canSubmit = validation ? validation.valid : true;

    const update = (patch: Partial<any>) => {
        setValues((v) => ({ ...v, ...patch }));
    };

    const transition = (result: StepResult<StepId>) => {
        switch (result.type) {
            case "next": {
                const stepIds = Object.keys(flow.steps) as StepId[];
                const nextStepIndex = stepIds.indexOf(currentStepId) + 1;
                setCurrentStepId(stepIds[nextStepIndex]);
                break;
            }
            case "goto":
            case "branch":
                setCurrentStepId(result.stepId);
                break;
            case "end":
                if (process.env.NODE_ENV === 'development') {
                    console.log("Done", values);
                }
                break;
        }
    };

    const submit = (overrideValues?: Partial<TValues>) => {
        if (!canSubmit) return;
        const effectiveValues = {
            ...values,
            ...overrideValues,
        };
        const result: StepResult<StepId> = step.onSubmit ? step.onSubmit(effectiveValues as any, effectiveValues) : { type: "next" };
        transition(result);
    };

    const StepComponent = step.render;

    return (
        <StepComponent
            values={values as any}
            update={update}
            submit={submit}
            canSubmit={canSubmit}
            validation={validation}
            allValues={values}
            externallyManagedState={externalState}
        />
    );
}
